import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Eye, EyeOff, Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
  Type, HelpCircle, Image as ImageIcon, Link as LinkIcon, Youtube, Clock, Calendar as CalendarIcon,
  Users, Award, Quote as QuoteIcon, Map as MapIcon, Ticket, BarChart3, Loader2, Upload, X, ExternalLink, Code
} from 'lucide-react';
import EventPageBlocks from '../components/EventPageBlocks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  Type, HelpCircle, Image: ImageIcon, Link: LinkIcon, Youtube, Clock,
  CalendarClock: CalendarIcon, Users, Award, Quote: QuoteIcon, Map: MapIcon, Ticket, BarChart3, Code,
};

const newId = () => `blk_${Math.random().toString(36).slice(2, 12)}`;

const DEFAULT_DATA = {
  hero: { eyebrow: '', title_override: '', tagline_override: '', image_url: '', alignment: 'center' },
  text: { heading: '', content: 'Tell your story here. Use **bold** or *italic* for emphasis. Separate paragraphs with a blank line.' },
  faq: { heading: 'Frequently Asked Questions', items: [{ q: 'What time should I arrive?', a: 'Doors open 30 minutes before showtime.' }] },
  gallery: { heading: 'Gallery', images: [] },
  links: { heading: 'Links', items: [{ title: '', url: '', description: '' }] },
  youtube: { heading: '', url: '', title: '' },
  countdown: { heading: 'Event starts in', target_datetime: '' },
  schedule: { heading: 'Schedule', items: [{ time: '7:00 PM', title: 'Doors open', description: '' }] },
  lineup: { heading: 'Lineup', items: [{ name: '', role: '', image_url: '', set_time: '' }] },
  sponsors: { heading: 'Our Sponsors', items: [{ name: '', logo_url: '', url: '' }] },
  testimonials: { heading: 'What People Say', items: [{ quote: '', author: '', source: '' }] },
  map: { heading: 'Location', address: '' },
  cta: { heading: 'Ready to join us?', text: 'Tickets are going fast.', button_text: 'Get Tickets', button_url: '#tickets' },
  stats: { heading: '', items: [{ number: '500+', label: 'Attendees last year' }] },
  html_page: {
    html: '<!-- Paste your full custom HTML here. It will replace the entire event page. -->\n<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0a0a0a;color:#fff;font-family:system-ui;">\n  <div style="text-align:center;padding:2rem;">\n    <h1 style="font-size:3rem;margin-bottom:1rem;">Your Custom Page</h1>\n    <p style="opacity:.7">Edit this HTML in the Page Builder.</p>\n  </div>\n</div>',
    show_floating_ticket_button: true,
    ticket_button_label: 'Buy Tickets',
    ticket_button_url: '#tickets',
    hide_global_header: false,
    hide_global_footer: false,
  },
};

export default function EventPageBuilder() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [event, setEvent] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [blockTypes, setBlockTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (user && !['admin', 'organization'].includes(user.role)) { navigate('/'); return; }
    (async () => {
      try {
        const [evRes, pbRes, btRes] = await Promise.all([
          axios.get(`${API}/hosted-events?status=all&limit=200`),
          axios.get(`${API}/events/${eventId}/page-builder`),
          axios.get(`${API}/page-builder/block-types`),
        ]);
        setEvent((evRes.data.events || []).find(e => e.event_id === eventId));
        setBlocks(pbRes.data.blocks || []);
        setEnabled(!!pbRes.data.enabled);
        setBlockTypes(btRes.data.types || []);
      } catch (e) { toast.error('Failed to load page builder'); }
      finally { setLoading(false); }
    })();
  }, [eventId, user]);

  // Ensure a locked hero block is always present at index 0
  useEffect(() => {
    if (loading) return;
    if (blocks.length === 0 || blocks[0]?.type !== 'hero') {
      const heroExists = blocks.find(b => b.type === 'hero');
      const others = blocks.filter(b => b.type !== 'hero');
      const hero = heroExists || { block_id: newId(), type: 'hero', data: { ...DEFAULT_DATA.hero }, is_visible: true };
      setBlocks([hero, ...others]);
    }
    // eslint-disable-next-line
  }, [loading]);

  const addBlock = (type) => {
    setBlocks(p => [...p, { block_id: newId(), type, data: { ...DEFAULT_DATA[type] }, is_visible: true }]);
  };
  const removeBlock = (id) => setBlocks(p => {
    const tgt = p.find(b => b.block_id === id);
    if (tgt?.type === 'hero') return p; // locked
    return p.filter(b => b.block_id !== id);
  });
  const updateBlock = (id, patch) => setBlocks(p => p.map(b => b.block_id === id ? { ...b, ...patch, data: { ...b.data, ...(patch.data || {}) } } : b));
  const moveBlock = (id, dir) => setBlocks(p => {
    const idx = p.findIndex(b => b.block_id === id);
    const ni = idx + dir;
    if (idx < 0 || ni < 0 || ni >= p.length) return p;
    if (p[idx]?.type === 'hero' || p[ni]?.type === 'hero') return p; // hero is locked at index 0
    const arr = [...p];
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    return arr;
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/events/${eventId}/page-builder`, { enabled, blocks }, { headers });
      toast.success('Page saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className="min-h-screen bg-background pt-16 pb-20" data-testid="event-page-builder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 pt-4 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/events`)} data-testid="pb-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-2xl font-bold">Page Builder</h1>
            <p className="text-sm text-muted-foreground truncate">{event?.name || 'Event'}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border">
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="pb-toggle" />
              <Label className="text-xs cursor-pointer">{enabled ? 'Public — Live' : 'Public — Off'}</Label>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPreviewMode(p => !p)} data-testid="pb-preview-toggle">
              {previewMode ? <><EyeOff className="h-4 w-4 mr-1.5" /> Edit</> : <><Eye className="h-4 w-4 mr-1.5" /> Preview</>}
            </Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="pb-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>

        {previewMode ? (
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-10" data-testid="pb-preview">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">Preview</p>
            {blocks.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No blocks yet — switch to Edit mode and add some.</p>
            ) : (
              <EventPageBlocks blocks={blocks.filter(b => b.is_visible)} event={event} />
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            {/* Block palette */}
            <div className="space-y-2 lg:sticky lg:top-20 self-start" data-testid="pb-palette">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Add Block</p>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                {blockTypes.filter(bt => bt.type !== 'hero').map(bt => {
                  const Icon = ICONS[bt.icon] || Type;
                  return (
                    <button
                      key={bt.type}
                      onClick={() => addBlock(bt.type)}
                      className="border border-border rounded-lg p-3 text-left hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-colors flex items-start gap-3"
                      data-testid={`pb-add-${bt.type}`}
                    >
                      <div className="p-1.5 rounded-md bg-indigo-500/10 flex-shrink-0">
                        <Icon className="h-4 w-4 text-indigo-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold">{bt.label}</p>
                        <p className="text-[10px] text-muted-foreground line-clamp-2">{bt.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Block list */}
            <div className="space-y-3" data-testid="pb-blocks">
              {blocks.length === 0 && (
                <Card className="border-dashed text-center py-16">
                  <CardContent>
                    <Type className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm font-medium">No blocks yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Pick a block from the left to get started.</p>
                  </CardContent>
                </Card>
              )}
              {blocks.map((b, i) => (
                <BlockEditor
                  key={b.block_id}
                  block={b}
                  index={i}
                  total={blocks.length}
                  token={token}
                  onChange={(patch) => updateBlock(b.block_id, patch)}
                  onRemove={() => removeBlock(b.block_id)}
                  onMove={(d) => moveBlock(b.block_id, d)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** ====== Per-block editor ====== */
const BlockEditor = ({ block, index, total, token, onChange, onRemove, onMove }) => {
  const { type, data, is_visible } = block;
  const slug = `${type}-${index}`;
  const setData = (patch) => onChange({ data: patch });
  const isLocked = type === 'hero';

  return (
    <Card className={`border-border ${is_visible ? '' : 'opacity-60'} ${isLocked ? 'border-indigo-500/40 bg-indigo-500/5' : ''}`} data-testid={`pb-block-${slug}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          {isLocked ? <Badge variant="outline" className="text-[10px] bg-indigo-500/10 border-indigo-500/30 text-indigo-500">🔒 Locked</Badge> : <GripVertical className="h-4 w-4 text-muted-foreground" />}
          <Badge variant="outline" className="text-[10px] uppercase capitalize">{type}</Badge>
          <span className="text-xs text-muted-foreground">#{index + 1}</span>
          <div className="flex-1" />
          {!isLocked && (
            <>
              <button onClick={() => onChange({ is_visible: !is_visible })} title={is_visible ? 'Hide' : 'Show'} data-testid={`pb-toggle-${slug}`}>
                {is_visible ? <Eye className="h-4 w-4 text-muted-foreground" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              </button>
              <button onClick={() => onMove(-1)} disabled={index <= 1} className="disabled:opacity-30" data-testid={`pb-up-${slug}`}><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => onMove(1)} disabled={index === total - 1} className="disabled:opacity-30" data-testid={`pb-down-${slug}`}><ChevronDown className="h-4 w-4" /></button>
              <button onClick={onRemove} className="text-rose-500" data-testid={`pb-remove-${slug}`}><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </div>

        {/* Hero block editor (special) */}
        {type === 'hero' && (
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">This hero is auto-populated from your event details. Override any field below if you want a custom look.</p>
            <FieldRow label="Eyebrow / kicker (optional)">
              <Input value={data.eyebrow || ''} onChange={e => setData({ eyebrow: e.target.value })} placeholder="e.g. SUMMER 2026" />
            </FieldRow>
            <FieldRow label="Title override (leave empty to use event name)">
              <Input value={data.title_override || ''} onChange={e => setData({ title_override: e.target.value })} placeholder="" />
            </FieldRow>
            <FieldRow label="Tagline override">
              <Input value={data.tagline_override || ''} onChange={e => setData({ tagline_override: e.target.value })} placeholder="" />
            </FieldRow>
            <FieldRow label="Hero image (overrides event image)">
              <ImageUploadField token={token} value={data.image_url} onChange={url => setData({ image_url: url })} />
            </FieldRow>
            <FieldRow label="Text alignment">
              <select value={data.alignment || 'center'} onChange={e => setData({ alignment: e.target.value })} className="w-full h-9 px-3 border border-border rounded-md bg-background text-sm">
                <option value="center">Center</option>
                <option value="left">Left</option>
              </select>
            </FieldRow>
          </div>
        )}

        {/* Heading is shared by most blocks (skip for hero / cta) */}
        {!['cta', 'hero'].includes(type) && (
          <FieldRow label="Heading (optional)">
            <Input value={data.heading || ''} onChange={e => setData({ heading: e.target.value })} placeholder="Section heading" />
          </FieldRow>
        )}

        {/* Type-specific editor */}
        {type === 'text' && (
          <FieldRow label="Content (markdown)">
            <Textarea value={data.content || ''} onChange={e => setData({ content: e.target.value })} className="min-h-[120px] font-mono text-sm" />
          </FieldRow>
        )}

        {type === 'faq' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ q: '', a: '' }}
            renderRow={(it, set) => (
              <>
                <Input placeholder="Question" value={it.q} onChange={e => set({ q: e.target.value })} />
                <Textarea placeholder="Answer" value={it.a} onChange={e => set({ a: e.target.value })} className="min-h-[60px]" />
              </>
            )}
          />
        )}

        {type === 'gallery' && (
          <GalleryEditor images={data.images || []} onChange={images => setData({ images })} token={token} />
        )}

        {type === 'links' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ title: '', url: '', description: '' }}
            renderRow={(it, set) => (
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="Title" value={it.title} onChange={e => set({ title: e.target.value })} />
                <Input placeholder="https://…" value={it.url} onChange={e => set({ url: e.target.value })} />
                <Input placeholder="Description (optional)" value={it.description} onChange={e => set({ description: e.target.value })} className="sm:col-span-2" />
              </div>
            )}
          />
        )}

        {type === 'youtube' && (
          <>
            <FieldRow label="YouTube URL">
              <Input value={data.url || ''} onChange={e => setData({ url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
            </FieldRow>
            <p className="text-[11px] text-muted-foreground">Supports youtube.com/watch, youtu.be, /shorts and /embed URLs.</p>
          </>
        )}

        {type === 'countdown' && (
          <FieldRow label="Target date/time (leave empty to use the event start)">
            <Input type="datetime-local" value={data.target_datetime || ''} onChange={e => setData({ target_datetime: e.target.value })} />
          </FieldRow>
        )}

        {type === 'schedule' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ time: '', title: '', description: '' }}
            renderRow={(it, set) => (
              <div className="grid sm:grid-cols-[120px_1fr] gap-2">
                <Input placeholder="7:00 PM" value={it.time} onChange={e => set({ time: e.target.value })} />
                <Input placeholder="Title" value={it.title} onChange={e => set({ title: e.target.value })} />
                <Input placeholder="Description (optional)" value={it.description} onChange={e => set({ description: e.target.value })} className="sm:col-span-2" />
              </div>
            )}
          />
        )}

        {type === 'lineup' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ name: '', role: '', image_url: '', set_time: '' }}
            renderRow={(it, set) => (
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="Name" value={it.name} onChange={e => set({ name: e.target.value })} />
                <Input placeholder="Role (DJ, Host, …)" value={it.role} onChange={e => set({ role: e.target.value })} />
                <ImageUploadField token={token} value={it.image_url} onChange={(url) => set({ image_url: url })} />
                <Input placeholder="Set time (e.g. 9:30 PM)" value={it.set_time} onChange={e => set({ set_time: e.target.value })} />
              </div>
            )}
          />
        )}

        {type === 'sponsors' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ name: '', logo_url: '', url: '' }}
            renderRow={(it, set) => (
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="Sponsor name" value={it.name} onChange={e => set({ name: e.target.value })} />
                <Input placeholder="Website URL" value={it.url} onChange={e => set({ url: e.target.value })} />
                <ImageUploadField token={token} value={it.logo_url} onChange={url => set({ logo_url: url })} className="sm:col-span-2" />
              </div>
            )}
          />
        )}

        {type === 'testimonials' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ quote: '', author: '', source: '' }}
            renderRow={(it, set) => (
              <>
                <Textarea placeholder="Quote" value={it.quote} onChange={e => set({ quote: e.target.value })} className="min-h-[60px]" />
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input placeholder="Author" value={it.author} onChange={e => set({ author: e.target.value })} />
                  <Input placeholder="Source / role (optional)" value={it.source} onChange={e => set({ source: e.target.value })} />
                </div>
              </>
            )}
          />
        )}

        {type === 'map' && (
          <FieldRow label="Address">
            <Input value={data.address || ''} onChange={e => setData({ address: e.target.value })} placeholder="123 Main St, Denver, CO" />
          </FieldRow>
        )}

        {type === 'cta' && (
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={data.heading || ''} onChange={e => setData({ heading: e.target.value })} placeholder="Heading" />
            <Input value={data.text || ''} onChange={e => setData({ text: e.target.value })} placeholder="Subtext" />
            <Input value={data.button_text || ''} onChange={e => setData({ button_text: e.target.value })} placeholder="Button text" />
            <Input value={data.button_url || ''} onChange={e => setData({ button_url: e.target.value })} placeholder="Button URL or #tickets" />
          </div>
        )}

        {type === 'stats' && (
          <ListEditor
            items={data.items || []}
            onChange={items => setData({ items })}
            empty={{ number: '', label: '' }}
            renderRow={(it, set) => (
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="500+" value={it.number} onChange={e => set({ number: e.target.value })} />
                <Input placeholder="Label" value={it.label} onChange={e => set({ label: e.target.value })} />
              </div>
            )}
          />
        )}

        {type === 'html_page' && (
          <div className="space-y-3" data-testid="pb-html-page-editor">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-300 leading-relaxed">
              <strong>Full takeover:</strong> when this block is visible, the entire event page is replaced with your custom HTML. All other blocks below are ignored. Use the toggles to optionally hide the global navigation/footer for a fully immersive experience.
              <br /><br />
              <strong>Full HTML documents are supported.</strong> If you paste a complete page (with <code>&lt;!DOCTYPE&gt;</code>, <code>&lt;html&gt;</code>, <code>&lt;head&gt;</code>, or <code>&lt;script&gt;</code> tags) it will be rendered inside a sandboxed iframe so all of your scripts (Tailwind CDN, React/Babel, Eventbrite widget, etc.) execute correctly.
            </div>

            <FieldRow label="Custom HTML">
              <Textarea
                value={data.html || ''}
                onChange={e => setData({ html: e.target.value })}
                rows={14}
                className="font-mono text-xs"
                placeholder="<div>Your full HTML page goes here...</div>"
                data-testid="pb-html-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Tip: include <code>id="tickets"</code> on any element to make the floating button scroll to it.
              </p>
            </FieldRow>

            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border">
                <Label className="text-xs cursor-pointer">Hide global navigation header</Label>
                <Switch
                  checked={!!data.hide_global_header}
                  onCheckedChange={(v) => setData({ hide_global_header: v })}
                  data-testid="pb-html-hide-header"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border">
                <Label className="text-xs cursor-pointer">Hide global footer</Label>
                <Switch
                  checked={!!data.hide_global_footer}
                  onCheckedChange={(v) => setData({ hide_global_footer: v })}
                  data-testid="pb-html-hide-footer"
                />
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border">
                <div>
                  <Label className="text-xs cursor-pointer">Floating "Buy Tickets" button</Label>
                  <p className="text-[10px] text-muted-foreground">Hovers on the right edge of the page.</p>
                </div>
                <Switch
                  checked={!!data.show_floating_ticket_button}
                  onCheckedChange={(v) => setData({ show_floating_ticket_button: v })}
                  data-testid="pb-html-show-float"
                />
              </div>
              {data.show_floating_ticket_button && (
                <div className="grid sm:grid-cols-2 gap-2">
                  <FieldRow label="Button label">
                    <Input
                      value={data.ticket_button_label || ''}
                      onChange={e => setData({ ticket_button_label: e.target.value })}
                      placeholder="Buy Tickets"
                      data-testid="pb-html-btn-label"
                    />
                  </FieldRow>
                  <FieldRow label="Button URL or anchor">
                    <Input
                      value={data.ticket_button_url || ''}
                      onChange={e => setData({ ticket_button_url: e.target.value })}
                      placeholder="#tickets or https://..."
                      data-testid="pb-html-btn-url"
                    />
                  </FieldRow>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** Reusable repeating-list editor (FAQ, Schedule, Sponsors, etc.) */
const ListEditor = ({ items, onChange, empty, renderRow }) => {
  const setItem = (idx, patch) => onChange(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    onChange(arr);
  };
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={idx} className="border border-border rounded-lg p-3 space-y-2 relative" data-testid={`pb-list-item-${idx}`}>
          <div className="space-y-2">{renderRow(it, (patch) => setItem(idx, patch))}</div>
          <div className="flex items-center justify-end gap-1 pt-1 border-t border-border">
            <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="disabled:opacity-30 p-1"><ChevronUp className="h-3.5 w-3.5" /></button>
            <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="disabled:opacity-30 p-1"><ChevronDown className="h-3.5 w-3.5" /></button>
            <button onClick={() => removeItem(idx)} className="text-rose-500 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, { ...empty }])} data-testid="pb-list-add">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add item
      </Button>
    </div>
  );
};

const FieldRow = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

/** Image upload helper using existing /api/admin/upload endpoint */
const ImageUploadField = ({ token, value, onChange, className = '' }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = `${API}/files/${res.data.storage_path}`;
      onChange(url);
    } catch (e) { toast.error('Upload failed — paste a URL instead'); }
    finally { setBusy(false); }
  };
  return (
    <div className={`space-y-1.5 ${className}`}>
      {value && (
        <div className="relative w-full h-24 rounded-lg overflow-hidden bg-muted">
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange('')} className="absolute top-1 right-1 bg-black/60 text-white rounded p-1"><X className="h-3 w-3" /></button>
        </div>
      )}
      <div className="flex gap-2">
        <Input type="file" accept="image/*" onChange={e => upload(e.target.files?.[0])} className="text-xs h-8" disabled={busy} />
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground self-center" />}
      </div>
      <Input placeholder="Or paste image URL" value={value || ''} onChange={e => onChange(e.target.value)} className="text-xs h-8" />
    </div>
  );
};

const GalleryEditor = ({ images, onChange, token }) => {
  const [busy, setBusy] = useState(false);
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/admin/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({ url: `${API}/files/${res.data.storage_path}`, caption: '' });
      }
      onChange([...(images || []), ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? 's' : ''}`);
    } catch (e) { toast.error('Some uploads failed'); }
    finally { setBusy(false); }
  };
  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));
  const setCaption = (idx, caption) => onChange(images.map((im, i) => i === idx ? { ...im, caption } : im));

  return (
    <div className="space-y-3" data-testid="pb-gallery-editor">
      <label className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer block hover:border-indigo-500/40">
        <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm font-medium">Click or drop images here</p>
        <p className="text-xs text-muted-foreground">Multiple files allowed</p>
        <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} disabled={busy} data-testid="pb-gallery-input" />
      </label>
      {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Uploading…</div>}
      {(images || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {images.map((img, i) => (
            <div key={i} className="relative border border-border rounded-lg overflow-hidden">
              <img src={img.url} alt="" className="w-full h-24 object-cover" />
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded p-1"><X className="h-3 w-3" /></button>
              <Input value={img.caption || ''} onChange={e => setCaption(i, e.target.value)} placeholder="Caption" className="text-[11px] h-7 border-0 rounded-none" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
